import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Modal, Button } from '@/components/ui'
import { Alert } from '@/components/ui/Alert'
import { SignaturePad } from '@/components/ui/SignaturePad'
// WEB-H7: PDF generator is dynamic-imported at the call site (pdfme weight).
import type { Invoice, AccountSettings } from '@/types'
import type { Template } from '@pdfme/common'

interface SignInvoiceModalProps {
  isOpen: boolean
  onClose: () => void
  onSign: (signedPdfKey: string) => Promise<void>
  loading?: boolean
  title?: string
  invoice?: Invoice | null
  template?: Template | null
  accountSettings?: AccountSettings | null
  signerName?: string
}

export function SignInvoiceModal({ isOpen, onClose, onSign, loading, title, invoice, template, accountSettings, signerName }: SignInvoiceModalProps) {
  const { t } = useTranslation('invoices')
  const { t: tc } = useTranslation('common')
  const [signatureData, setSignatureData] = useState<string | null>(null)
  const [isGenerating, setIsGenerating] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSign = async () => {
    if (!signatureData) return
    setError(null)

    if (invoice && template) {
      setIsGenerating(true)
      try {
        // Generate signed PDF with signature overlay and upload to Tigris
        const { generateAndUploadSignedInvoicePdf } = await import('@/lib/invoicePdfGenerator')
        const key = await generateAndUploadSignedInvoicePdf(
          invoice,
          template,
          signatureData,
          signerName || 'Signer',
          accountSettings ?? undefined,
        )
        setIsGenerating(false)
        await onSign(key)
      } catch (err) {
        setIsGenerating(false)
        setError(err instanceof Error ? err.message : tc('errors.something_went_wrong'))
      }
    } else {
      // Bulk mode: pass signature data URL so caller can generate each PDF
      try {
        await onSign(signatureData)
      } catch (err) {
        setError(err instanceof Error ? err.message : tc('errors.something_went_wrong'))
      }
    }
  }

  const handleClose = () => {
    setSignatureData(null)
    setError(null)
    setIsGenerating(false)
    onClose()
  }

  const isBusy = isGenerating || loading

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={title || t('actions.sign')}
      footer={
        <>
          <Button variant="ghost" onClick={handleClose} disabled={isBusy}>
            {tc('actions.cancel')}
          </Button>
          <Button
            onClick={handleSign}
            loading={isBusy}
            disabled={!signatureData}
          >
            {isGenerating ? t('sign_modal.generating') : loading ? t('sign_modal.signing') : t('actions.sign')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col items-center gap-4">
        <p className="text-sm text-base-content/60 text-center">
          {t('sign_modal.description')}
        </p>
        <SignaturePad
          onChange={setSignatureData}
          width={380}
          height={200}
          disabled={isBusy}
        />
        {error && (
          <Alert variant="error" icon={null} role="alert" className="py-2 px-3 w-full">
            <span className="text-sm">{error}</span>
          </Alert>
        )}
      </div>
    </Modal>
  )
}
